import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { saveFile } from '../../utils/saveFile';
import {
  FaHospital, FaUserMd, FaCalendarAlt, FaCheckCircle, FaTimesCircle,
  FaAward, FaBook, FaClipboardList, FaHeartbeat, FaTimes, FaSearch,
  FaSpinner, FaEye, FaHistory, FaUpload, FaDownload, FaChevronDown,
  FaChevronUp, FaExclamationTriangle
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isCompleted = (toDate) => {
  if (!toDate) return false;
  const t = new Date(toDate); t.setHours(0, 0, 0, 0);
  const n = new Date(); n.setHours(0, 0, 0, 0);
  return t < n;
};

const RecordRow = ({ label, value, sub }) => (
  <div style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
    <span style={{ fontSize: 12, color: '#888', minWidth: 90 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#333', flex: 1 }}>
      {value}
      {sub && <span style={{ fontSize: 11, color: '#777', display: 'block' }}>{sub}</span>}
    </span>
  </div>
);

// ─── History DRP card for HOD ─────────────────────────────────────────────────
const HistoryDRPCard = ({ hist, index, studentId, token, onDownloadSystemCert }) => {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const v = hist.inchargeVerification || {};
  const hasCert = hist.certificate?.issued || hist.uploadedCertificate?.fileName;

  const downloadUploadedCert = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/classic-drp/hod/student/${studentId}/history/${hist._id}/certificate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) {
        await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
      }
    } catch { /* silent */ }
    setDownloading(false);
  };

  return (
    <div style={{ border: hasCert ? '1px solid #e5e7eb' : '1px solid #fca5a5', borderRadius: 10, padding: 12, marginBottom: 8, background: hasCert ? '#fafafa' : '#fff9f9' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ fontSize: 11, padding: '3px 7px', borderRadius: 8, background: hasCert ? '#d1fae5' : '#fee2e2', color: hasCert ? '#065f46' : '#b91c1c', fontWeight: 700, flexShrink: 0 }}>
          DRP {index}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{hist.profile?.hospitalName || '—'}</div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                <FaCalendarAlt size={10} style={{ marginRight: 4 }} />
                {formatDate(hist.profile?.fromDate)} – {formatDate(hist.profile?.toDate)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                <FaUserMd size={10} style={{ marginRight: 4 }} />
                {hist.profile?.inchargeName || (hist.profile?.inchargeId ? 'Incharge assigned' : <em>No Incharge</em>)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
              {hist.certificate?.issued && <span style={styles.badgeSystemCert}><FaAward size={9} style={{ marginRight: 3 }} />System Cert</span>}
              {hist.uploadedCertificate?.fileName && <span style={styles.badgeUploadedCert}><FaUpload size={9} style={{ marginRight: 3 }} />Uploaded Cert</span>}
              {!hasCert && <span style={styles.badgeNoCert}><FaExclamationTriangle size={9} style={{ marginRight: 3 }} />No Certificate</span>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'attendanceVerified', label: 'Att' },
              { key: 'diaryVerified', label: 'Diary' },
              { key: 'operationsVerified', label: 'Ops' },
              { key: 'publicHealthVerified', label: 'PH' }
            ].map(item => (
              <span key={item.key} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: v[item.key] ? '#d1fae5' : '#f3f4f6', color: v[item.key] ? '#065f46' : '#999' }}>
                {v[item.key] ? '✓' : '○'} {item.label}
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {hist.certificate?.issued && (
              <button onClick={() => onDownloadSystemCert(hist)} style={styles.miniBtn}>
                <FaDownload size={10} style={{ marginRight: 4 }} /> System Certificate
              </button>
            )}
            {hist.uploadedCertificate?.fileName && (
              <button onClick={downloadUploadedCert} disabled={downloading} style={{ ...styles.miniBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                {downloading ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload size={10} style={{ marginRight: 4 }} />}
                Uploaded Certificate
              </button>
            )}
            <button onClick={() => setExpanded(x => !x)} style={{ ...styles.miniBtn, background: '#f3f4f6', color: '#555', border: '1px solid #e5e7eb' }}>
              {expanded ? <FaChevronUp size={10} style={{ marginRight: 4 }} /> : <FaChevronDown size={10} style={{ marginRight: 4 }} />}
              {expanded ? 'Hide' : 'Details'}
            </button>
          </div>

          {expanded && (
            <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, marginBottom: 8 }}>
                <div><span style={{ color: '#888' }}>Attendance: </span><strong>{hist.attendance?.length || 0} days</strong></div>
                <div><span style={{ color: '#888' }}>Diary: </span><strong>{hist.diary?.length || 0} entries</strong></div>
                <div><span style={{ color: '#888' }}>Operations: </span><strong>{hist.operations?.length || 0}</strong></div>
                <div><span style={{ color: '#888' }}>Public Health: </span><strong>{hist.publicHealth?.length || 0}</strong></div>
              </div>
              {v.remarks && (
                <div style={{ padding: '8px 10px', background: '#f8f8f8', borderRadius: 8, borderLeft: '3px solid #0f766e', fontSize: 12, marginBottom: 8 }}>
                  <strong>Incharge Remarks:</strong> {v.remarks}
                </div>
              )}
              {hist.certificate?.issuedAt && <div style={{ fontSize: 12, color: '#059669', marginBottom: 4 }}>System cert issued: {formatDate(hist.certificate.issuedAt)}</div>}
              {hist.uploadedCertificate?.uploadedAt && (
                <div style={{ fontSize: 12, color: '#2563eb' }}>
                  Uploaded: {formatDate(hist.uploadedCertificate.uploadedAt)}
                  {hist.uploadedCertificate.fileName && <span style={{ color: '#666' }}> ({hist.uploadedCertificate.fileName})</span>}
                </div>
              )}

              {/* Diary records */}
              {expanded && hist.diary?.length > 0 && (
                <details style={{ marginTop: 10 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#2563eb' }}>Daily Diary ({hist.diary.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.diary.map((d, i) => (
                      <RecordRow key={i} label={`${formatDate(d.date)}${d.day ? ` (${d.day})` : ''}`} value={d.entry} />
                    ))}
                  </div>
                </details>
              )}
              {/* Operations */}
              {expanded && hist.operations?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#7c3aed' }}>Operations ({hist.operations.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.operations.map((op, i) => (
                      <RecordRow key={i} label={formatDate(op.date)} value={`${op.patientName}${op.ipNumber ? ` · IP: ${op.ipNumber}` : ''}`} sub={op.category} />
                    ))}
                  </div>
                </details>
              )}
              {/* Public Health */}
              {expanded && hist.publicHealth?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc6803' }}>Public Health ({hist.publicHealth.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.publicHealth.map((ph, i) => (
                      <RecordRow key={i} label={formatDate(ph.date)} value={ph.activity} />
                    ))}
                  </div>
                </details>
              )}
              {/* Attendance */}
              {expanded && hist.attendance?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#16a085' }}>Attendance ({hist.attendance.length})</summary>
                  <div style={{ marginTop: 6 }}>
                    {hist.attendance.map((a, i) => (
                      <RecordRow key={i} label={formatDate(a.date)} value={a.posting} />
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClassicDRPMonitor = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [certDownloading, setCertDownloading] = useState(null);

  const token = localStorage.getItem('token');

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/classic-drp/hod/students`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setStudents(d.students);
    } catch { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const viewStudent = async (studentId) => {
    setDetailLoading(true);
    try {
      const r = await fetch(`${API}/classic-drp/hod/student/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setSelectedStudent(d.entry);
    } catch { /* silent */ }
    setDetailLoading(false);
  };

  const downloadSystemCert = async (profileData, studentInfo) => {
    // generateClassicDRPCertificate is not imported here; we call it via the student side
    // For HOD, we just trigger the same PDF generation
    try {
      const { generateClassicDRPCertificate } = await import('../../utils/classicDrpCertificateGenerator');
      const studentName = `${studentInfo?.profile?.firstName || ''} ${studentInfo?.profile?.lastName || ''}`.trim();
      const courseName = studentInfo?.assignedCourses?.[0]?.courseName || '';
      const inchargeData = profileData?.inchargeId;
      const inchargeName = profileData?.inchargeName ||
        (inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : '');
      await generateClassicDRPCertificate(
        { studentName, courseName, hospitalName: profileData?.hospitalName, fromDate: profileData?.fromDate, toDate: profileData?.toDate, inchargeName },
        collegeConfig.collegeName, collegeConfig.logo
      );
    } catch { /* silent */ }
  };

  const downloadUploadedCert = async (studentId, historyId) => {
    const path = historyId
      ? `/hod/student/${studentId}/history/${historyId}/certificate`
      : `/hod/student/${studentId}/certificate`;
    const key = historyId || 'current';
    setCertDownloading(key);
    try {
      const r = await fetch(`${API}/classic-drp${path}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
      }
    } catch { /* silent */ }
    setCertDownloading(null);
  };

  const filtered = students.filter(item => {
    if (!search) return true;
    const s = item.student;
    const name = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.toLowerCase();
    return name.includes(search.toLowerCase()) || (s?.profile?.registrationNumber || '').toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return (
    <div style={styles.centered}>
      <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 24, color: '#b11217' }} />
      <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
    </div>
  );

  // ── Detail View ──────────────────────────────────────────────────────────────
  if (selectedStudent) {
    const s = selectedStudent.student;
    const v = selectedStudent.inchargeVerification || {};
    const inchargeData = selectedStudent.profile?.inchargeId;
    const inchargeName = inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : '—';
    const studentName = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.trim();
    const drpHistory = selectedStudent.drpHistory || [];
    const totalDRPs = drpHistory.length + (selectedStudent.profile?.hospitalName ? 1 : 0);
    const currentCompleted = isCompleted(selectedStudent.profile?.toDate);
    const studentId = s?._id;

    return (
      <div style={styles.modal}>
        <div style={styles.modalContent}>
          <div style={styles.modalHeader}>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{studentName}</h2>
              <span style={{ fontSize: 12, color: '#888' }}>
                {s?.assignedCourses?.[0]?.courseName || ''} · {s?.profile?.registrationNumber || ''}
                {totalDRPs > 1 && <span style={{ marginLeft: 8, background: '#ede9fe', color: '#7c3aed', padding: '1px 7px', borderRadius: 10, fontWeight: 600 }}>{totalDRPs} DRPs</span>}
              </span>
            </div>
            <button onClick={() => setSelectedStudent(null)} style={styles.closeBtn}><FaTimes /></button>
          </div>

          <div style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 70px)', padding: 16 }}>

            {/* ── Current DRP ─────────────────────────────────────────────── */}
            {selectedStudent.profile?.hospitalName && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <FaHospital style={{ color: '#b11217' }} />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Current DRP</span>
                  {currentCompleted && (
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#d1fae5', color: '#065f46', fontWeight: 600 }}>Completed</span>
                  )}
                </div>

                <div style={styles.infoCard}>
                  <div style={styles.detailGrid}>
                    <div><span style={styles.dLabel}>Hospital</span><span style={styles.dVal}>{selectedStudent.profile?.hospitalName || '—'}</span></div>
                    <div><span style={styles.dLabel}>Duration</span><span style={styles.dVal}>{formatDate(selectedStudent.profile?.fromDate)} – {formatDate(selectedStudent.profile?.toDate)}</span></div>
                    <div><span style={styles.dLabel}>Incharge</span><span style={styles.dVal}>{inchargeName}</span></div>
                    <div>
                      <span style={styles.dLabel}>Certificate</span>
                      <span style={{ ...styles.dVal }}>
                        {selectedStudent.certificate?.issued ? (
                          <span style={{ color: '#059669' }}>System — Issued {formatDate(selectedStudent.certificate.issuedAt)}</span>
                        ) : selectedStudent.uploadedCertificate?.fileName ? (
                          <span style={{ color: '#2563eb' }}>Uploaded — {formatDate(selectedStudent.uploadedCertificate.uploadedAt)}</span>
                        ) : (
                          <span style={{ color: '#888' }}>Not issued</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Certificate download for HOD */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {selectedStudent.certificate?.issued && (
                    <button onClick={() => downloadSystemCert(selectedStudent.profile, s)} style={styles.miniBtn}>
                      <FaDownload size={10} style={{ marginRight: 4 }} /> System Certificate
                    </button>
                  )}
                  {selectedStudent.uploadedCertificate?.fileName && (
                    <button onClick={() => downloadUploadedCert(studentId, null)} disabled={certDownloading === 'current'} style={{ ...styles.miniBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                      {certDownloading === 'current' ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload size={10} style={{ marginRight: 4 }} />}
                      Uploaded Certificate ({selectedStudent.uploadedCertificate.fileName})
                    </button>
                  )}
                </div>

                {/* Verification status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                  {[
                    { key: 'attendanceVerified', label: 'Attendance' },
                    { key: 'diaryVerified', label: 'Diary' },
                    { key: 'operationsVerified', label: 'Operations' },
                    { key: 'publicHealthVerified', label: 'Public Health' }
                  ].map(item => (
                    <div key={item.key} style={{ ...styles.badge, background: v[item.key] ? '#d1fae5' : '#fee2e2' }}>
                      {v[item.key] ? <FaCheckCircle style={{ color: '#059669', marginRight: 6 }} /> : <FaTimesCircle style={{ color: '#dc2626', marginRight: 6 }} />}
                      <span style={{ fontSize: 12 }}>{item.label}</span>
                    </div>
                  ))}
                </div>

                {v.remarks && (
                  <div style={{ marginBottom: 14, padding: 10, background: '#f8f8f8', borderRadius: 8, borderLeft: '3px solid #0f766e', fontSize: 13 }}>
                    <strong>Incharge Remarks:</strong> {v.remarks}
                  </div>
                )}

                {/* Current DRP Records */}
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}><FaCalendarAlt style={{ marginRight: 8, color: '#16a085' }} />Attendance ({selectedStudent.attendance?.length || 0})</h3>
                  {selectedStudent.attendance?.length === 0 ? <p style={styles.empty}>No records</p> : selectedStudent.attendance.map(a => (
                    <RecordRow key={a._id} label={formatDate(a.date)} value={a.posting} />
                  ))}
                </div>
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}><FaBook style={{ marginRight: 8, color: '#2563eb' }} />Daily Diary ({selectedStudent.diary?.length || 0})</h3>
                  {selectedStudent.diary?.length === 0 ? <p style={styles.empty}>No records</p> : selectedStudent.diary.map(d => (
                    <RecordRow key={d._id} label={`${formatDate(d.date)} (${d.day || ''})`} value={d.entry} />
                  ))}
                </div>
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}><FaClipboardList style={{ marginRight: 8, color: '#7c3aed' }} />Operations ({selectedStudent.operations?.length || 0})</h3>
                  {selectedStudent.operations?.length === 0 ? <p style={styles.empty}>No records</p> : selectedStudent.operations.map(op => (
                    <RecordRow key={op._id} label={formatDate(op.date)} value={`${op.patientName}${op.ipNumber ? ` · IP: ${op.ipNumber}` : ''}`} sub={op.category} />
                  ))}
                </div>
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}><FaHeartbeat style={{ marginRight: 8, color: '#dc6803' }} />Public Health ({selectedStudent.publicHealth?.length || 0})</h3>
                  {selectedStudent.publicHealth?.length === 0 ? <p style={styles.empty}>No records</p> : selectedStudent.publicHealth.map(ph => (
                    <RecordRow key={ph._id} label={formatDate(ph.date)} value={ph.activity} />
                  ))}
                </div>
              </>
            )}

            {/* ── DRP History ──────────────────────────────────────────────── */}
            {drpHistory.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingTop: selectedStudent.profile?.hospitalName ? 8 : 0, borderTop: selectedStudent.profile?.hospitalName ? '1px solid #e5e7eb' : 'none' }}>
                  <FaHistory style={{ color: '#6366f1' }} />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Past DRP History</span>
                  <span style={{ marginLeft: 'auto', background: '#ede9fe', color: '#7c3aed', fontSize: 12, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                    {drpHistory.length} past DRP{drpHistory.length > 1 ? 's' : ''}
                  </span>
                </div>
                {[...drpHistory].reverse().map((hist, idx) => (
                  <HistoryDRPCard
                    key={hist._id}
                    hist={hist}
                    index={drpHistory.length - idx}
                    studentId={studentId}
                    token={token}
                    onDownloadSystemCert={(h) => downloadSystemCert(h.profile || h, s)}
                    studentData={s}
                  />
                ))}
              </div>
            )}

            {!selectedStudent.profile?.hospitalName && drpHistory.length === 0 && (
              <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>
                <FaHospital style={{ fontSize: 32, color: '#ddd', marginBottom: 10 }} />
                <p>No DRP data recorded yet.</p>
              </div>
            )}
          </div>
        </div>
        <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
      </div>
    );
  }

  // ── Student List ──────────────────────────────────────────────────────────────
  return (
    <div style={{ marginTop: 24 }}>
      {detailLoading && (
        <div style={styles.loadingOverlay}>
          <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 28, color: '#b11217' }} />
          <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
        </div>
      )}

      <div style={styles.header}>
        <FaHospital style={{ color: '#E67E22', fontSize: 20 }} />
        <div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1a1a2e' }}>Classic DRP Activity Monitor</h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#666' }}>Read-only view of student DRP activities</p>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <span style={styles.statNum}>{students.length}</span>
          <span style={styles.statLabel}>Total Students</span>
        </div>
        <div style={styles.statCard}>
          <span style={{ ...styles.statNum, color: '#059669' }}>{students.filter(s => s.entry?.certificate?.issued || s.entry?.uploadedCertificate?.fileName).length}</span>
          <span style={styles.statLabel}>Certified</span>
        </div>
        <div style={styles.statCard}>
          <span style={{ ...styles.statNum, color: '#2563eb' }}>
            {students.filter(s => {
              const v = s.entry?.inchargeVerification || {};
              return v.attendanceVerified && v.diaryVerified && v.operationsVerified && v.publicHealthVerified && !s.entry?.certificate?.issued;
            }).length}
          </span>
          <span style={styles.statLabel}>Verified</span>
        </div>
        <div style={styles.statCard}>
          <span style={{ ...styles.statNum, color: '#7c3aed' }}>{students.filter(s => (s.entry?.drpHistory?.length || 0) > 0).length}</span>
          <span style={styles.statLabel}>Multi-DRP</span>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchBox}>
        <FaSearch style={{ color: '#999', position: 'absolute', left: 12, top: 11 }} />
        <input type="text" placeholder="Search by student name or registration number..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
      </div>

      {filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <FaUserMd style={{ fontSize: 40, color: '#ccc', marginBottom: 12 }} />
          <p style={{ color: '#999', fontSize: 14 }}>No Classic DRP data found for students in your course.</p>
          <p style={{ color: '#bbb', fontSize: 12 }}>Students will appear here once they start their Classic DRP activities.</p>
        </div>
      ) : (
        filtered.map(({ student: s, entry }) => {
          const v = entry?.inchargeVerification || {};
          const allVerified = v.attendanceVerified && v.diaryVerified && v.operationsVerified && v.publicHealthVerified;
          const certified = entry?.certificate?.issued || entry?.uploadedCertificate?.fileName;
          const studentName = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.trim();
          const inchargeData = entry?.profile?.inchargeId;
          const inchargeName = inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : null;
          const histCount = entry?.drpHistory?.length || 0;
          const totalDRPs = histCount + (entry?.profile?.hospitalName ? 1 : 0);
          const currentCompleted = isCompleted(entry?.profile?.toDate);

          return (
            <div key={s._id} style={styles.studentCard}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={styles.avatar}>{studentName.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>{studentName}</h3>
                      <p style={{ margin: '2px 0', fontSize: 12, color: '#666' }}>{s?.assignedCourses?.[0]?.courseName || ''} · {s?.profile?.registrationNumber || ''}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      {certified ? (
                        <span style={styles.badgeCert}>
                          {entry?.certificate?.issued ? <FaAward size={10} style={{ marginRight: 4 }} /> : <FaUpload size={10} style={{ marginRight: 4 }} />}
                          {entry?.certificate?.issued ? 'System Cert' : 'Uploaded Cert'}
                        </span>
                      ) : allVerified ? (
                        <span style={styles.badgeGreen}>All Verified</span>
                      ) : entry ? (
                        <span style={styles.badgeGray}>In Progress</span>
                      ) : (
                        <span style={styles.badgeGray}>No Data</span>
                      )}
                      {totalDRPs > 1 && (
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#ede9fe', color: '#7c3aed', fontWeight: 600 }}>
                          <FaHistory size={8} style={{ marginRight: 3 }} />{totalDRPs} DRPs
                        </span>
                      )}
                    </div>
                  </div>
                  {entry?.profile?.hospitalName && (
                    <p style={{ margin: '3px 0', fontSize: 12, color: '#888' }}>
                      <FaHospital size={10} style={{ marginRight: 4 }} />{entry.profile.hospitalName}
                      {inchargeName && <span style={{ marginLeft: 8 }}><FaUserMd size={10} style={{ marginRight: 4 }} />{inchargeName}</span>}
                      {currentCompleted && <span style={{ marginLeft: 8, color: '#059669', fontSize: 11 }}>(Completed)</span>}
                    </p>
                  )}
                  {entry?.profile?.fromDate && (
                    <p style={{ margin: '2px 0', fontSize: 12, color: '#888' }}>
                      <FaCalendarAlt size={10} style={{ marginRight: 4 }} />
                      {formatDate(entry.profile.fromDate)} – {formatDate(entry.profile.toDate)}
                    </p>
                  )}
                  {entry && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {[
                        { key: 'attendanceVerified', label: 'Attendance', count: entry.attendance?.length || 0 },
                        { key: 'diaryVerified', label: 'Diary', count: entry.diary?.length || 0 },
                        { key: 'operationsVerified', label: 'Ops', count: entry.operations?.length || 0 },
                        { key: 'publicHealthVerified', label: 'PH', count: entry.publicHealth?.length || 0 }
                      ].map(item => (
                        <span key={item.key} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: v[item.key] ? '#d1fae5' : '#f3f4f6', color: v[item.key] ? '#065f46' : '#666' }}>
                          {v[item.key] ? '✓' : '○'} {item.label} ({item.count})
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {entry && (
                <button onClick={() => viewStudent(s._id)} style={styles.viewBtn}>
                  <FaEye style={{ marginRight: 6 }} /> View Details{totalDRPs > 1 ? ` (${totalDRPs} DRPs)` : ''}
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const styles = {
  centered: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40, gap: 10 },
  loadingOverlay: { position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  header: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16, padding: '14px 16px', background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 12 },
  statsRow: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  statCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 20px', textAlign: 'center', flex: '1 0 80px' },
  statNum: { display: 'block', fontSize: 22, fontWeight: 800, color: '#b11217' },
  statLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px' },
  searchBox: { position: 'relative', marginBottom: 14 },
  searchInput: { width: '100%', padding: '9px 12px 9px 36px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  emptyState: { textAlign: 'center', padding: '40px 20px', color: '#999' },
  studentCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  avatar: { width: 40, height: 40, borderRadius: '50%', background: '#b11217', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flexShrink: 0 },
  viewBtn: { display: 'flex', alignItems: 'center', marginTop: 10, padding: '6px 14px', background: 'none', border: '1px solid #b11217', color: '#b11217', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  badgeCert: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#d97706', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeGreen: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#d1fae5', color: '#059669', fontWeight: 600 },
  badgeGray: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#f3f4f6', color: '#666', fontWeight: 500 },
  badgeSystemCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#d97706', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeUploadedCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeNoCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#fee2e2', color: '#dc2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  miniBtn: { fontSize: 12, padding: '5px 10px', borderRadius: 8, background: '#fff7ed', color: '#b45309', border: '1px solid #fed7aa', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 600 },
  // Modal
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modalContent: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #e5e7eb' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#666', padding: 4 },
  infoCard: { background: '#f9fafb', borderRadius: 10, padding: 12, marginBottom: 14 },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  dLabel: { display: 'block', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 },
  dVal: { display: 'block', fontSize: 13, color: '#1a1a2e', fontWeight: 500 },
  badge: { display: 'flex', alignItems: 'center', padding: '7px 10px', borderRadius: 8 },
  sectionBlock: { marginBottom: 14, background: '#f9fafb', borderRadius: 10, padding: 12 },
  sectionTitle: { margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#1a1a2e', display: 'flex', alignItems: 'center' },
  empty: { color: '#999', fontSize: 13, fontStyle: 'italic', margin: '4px 0' }
};

export default ClassicDRPMonitor;
